/**
 * HT Calculator State Manager v1.2 - PRODUCTION READY
 * Universal state extraction and persistence for Handy Tags calculators
 * 
 * DEPENDENCY: Requires either ht-calculator.js OR ht-calculator-styled.js
 * 
 * Purpose: Extract state from ANY calculator type and persist across page refreshes
 * Supports: Regular calculator, Styled calculator, Custom sizes, SessionStorage caching
 * 
 * Event Integration: Auto-saves on 'calculator:state-change' events
 * 
 * CHANGELOG v1.2:
 * - FIXED: Calculator-type-aware validation (styled vs regular)
 * - FIXED: Styled calculators no longer require minSize or separate size field
 * - FIXED: Style template dimensions validated instead of size field for styled
 * - Added smart validatePricingDataInDOM() - only validates visible/selected fields
 * - Added sanitizeRecoveredState() to fix "unknown" material values
 * - Added exportStateWithValidation() for strict validation before export
 * - Added showValidationError() for user-friendly error display
 * 
 * @author Nino
 */

(function() {
  'use strict';
  
  // Prevent double loading
  if (window.HTCalculatorStateManager) {
    return;
  }

  class HTCalculatorStateManager {
    constructor() {
      this.activeCalculatorType = null; // 'regular' | 'styled' | null
      this.calculator = null; // Reference to active calculator
      this.storageKey = 'ht-calculator-state';
      this.recoveryNotificationId = 'ht-state-recovery-notification';
      
      // State change debouncing to prevent excessive saves
      this.saveStateDebounced = this.debounce(this.saveState.bind(this), 300);
      
      this.init();
    }
    
    // ========================================
    // INITIALIZATION & CALCULATOR DETECTION
    // ========================================
    
    init() {
      this.detectActiveCalculator();
      this.bindEventListeners();
      this.attemptStateRecovery();
    }
    
    detectActiveCalculator() {
      // Priority: Styled calculator first (enhanced features)
      if (window.htCalculatorStyled && window.htCalculatorStyled.form) {
        this.activeCalculatorType = 'styled';
        this.calculator = window.htCalculatorStyled;
        return;
      }
      
      // Fallback: Regular calculator
      if (window.htCalculator && window.htCalculator.form) {
        this.activeCalculatorType = 'regular';
        this.calculator = window.htCalculator;
        return;
      }
      
      // Retry detection after DOM ready
      if (!this.calculator) {
        setTimeout(() => this.detectActiveCalculator(), 100);
      }
    }
    
    bindEventListeners() {
      // Listen for calculator state changes
      document.addEventListener('calculator:state-change', () => {
        this.saveStateDebounced();
      });
      
      // Emergency save on page unload
      window.addEventListener('beforeunload', () => {
        this.saveState();
      });
      
      // Listen for calculator ready events
      document.addEventListener('calculator:ready', () => {
        this.detectActiveCalculator();
      });
      // Listen for cart success to clear saved state
  // ═══════════════════════════════════════════════════════════════
  document.addEventListener('cart:item-added-success', () => {
    this.clearState();
  });
    }
    
    // ========================================
    // STATE EXTRACTION - UNIVERSAL ADAPTER
    // ========================================
    
    extractState() {
      if (!this.calculator) {
        console.warn('⚠️ No calculator available for state extraction');
        return null;
      }
      
      const baseState = {
        calculatorType: this.activeCalculatorType,
        timestamp: Date.now(),
        validationToken: window.htCartValidator?.getSessionToken(),
      };
      
      // Route to appropriate extraction method
      switch (this.activeCalculatorType) {
        case 'styled':
          return { ...baseState, ...this.extractStyledCalculatorState() };
        case 'regular':
          return { ...baseState, ...this.extractRegularCalculatorState() };
        default:
          console.error('❌ Unknown calculator type:', this.activeCalculatorType);
          return null;
      }
    }
    
    extractStyledCalculatorState() {
      const state = {
        // Core selections
        material: this.getFieldValue('material'),
        shape: this.getFieldValue('shape', 'style_template'), // Styled uses style_template
        style_template: this.getFieldValue('shape', 'style_template'), // Keep explicit
        quantity: this.getCurrentQuantity(),
        
        // Print options
        print_sides: this.getFieldValue('print_sides'),
        print_ink: this.getFieldValue('print_ink'),
        
        // Optional enhancements
        corner_style: this.getFieldValue('corner_style'),
        drill_diameter: this.getFieldValue('drill_diameter'),
        drill_position: this.getFieldValue('drill_position'),
        eyelet: this.getFieldValue('eyelet'),
        string_attachment: this.getFieldValue('string_attachment'),
        string_type: this.getFieldValue('string_type'),
        
        // Additional options (styled-specific)
        multiple_designs: this.getFieldValue('multiple_designs'),
        variable_data: this.getFieldValue('variable_data'),
        sequential_data: this.getFieldValue('sequential_numbers'),
        sequential_range: this.getFieldValue('sequential_range'), // 2025-11-26: Sequential range values
        perforation: this.getCheckboxValue('perforation'),
        design_my_artwork: this.getCheckboxValue('design_my_artwork') || false,
      };
      
      // Handle custom size for styled calculator
      if (this.isCustomSizeSelected()) {
        state.custom_size = this.extractCustomSize();
      }
      
      // Add pricing data with underscore prefix (hidden properties)
      const pricingData = this.extractPricingData();
      if (pricingData) {
        state._price_total_pence = pricingData.totalInclVATPence;      // UNCHANGED: inc-VAT for GA4/display
        state._price_total_pence_ex_vat = pricingData.totalExVATPence;  // NEW: ex-VAT for Vercel/Shopify
        state._price_per_tag_pence = pricingData.pricePerTagPence;
        state._vat_amount_pence = pricingData.vatAmountPence;
      }

      return state;
    }

    extractRegularCalculatorState() {
      const state = {
        // Core selections
        material: this.getFieldValue('material'),
        shape: this.getFieldValue('shape'),
        size: this.getFieldValue('size'),
        quantity: this.getCurrentQuantity(),
        
        // Print options
        print_sides: this.getFieldValue('print_sides'),
        print_ink: this.getFieldValue('print_ink'),
        
        // Optional enhancements
        corner_style: this.getFieldValue('corner_style'),
        drill_hole: this.getFieldValue('drill_hole'),
        drill_position: this.getFieldValue('drill_position'),
        eyelet: this.getFieldValue('eyelet'),
        string_attachment: this.getFieldValue('string_attachment'),
        string_type: this.getFieldValue('string_type'),
        
        // Additional options (via wizard if present)
        multiple_designs: this.getAdditionalOptionValue('multiple_designs'),
        variable_data: this.getAdditionalOptionValue('variable_data'),
        sequential_data: this.getAdditionalOptionValue('sequential_data'),
        sequential_range: this.getFieldValue('sequential_range'), // 2025-11-26: Sequential range values
        perforation: this.getAdditionalOptionValue('perforation'),
        design_my_artwork: this.getCheckboxValue('design_my_artwork') || false,
      };

      // Handle custom size for regular calculator
      if (this.isCustomSizeSelected()) {
        state.custom_size = this.extractCustomSize();
      }
      
      // Add pricing data with underscore prefix
      const pricingData = this.extractPricingData();
      if (pricingData) {
        state._price_total_pence = pricingData.totalInclVATPence;      // UNCHANGED: inc-VAT for GA4/display
        state._price_total_pence_ex_vat = pricingData.totalExVATPence;  // NEW: ex-VAT for Vercel/Shopify
        state._price_per_tag_pence = pricingData.pricePerTagPence;
        state._vat_amount_pence = pricingData.vatAmountPence;
      }
      
      return state;
    }
    
    // ========================================
    // FIELD VALUE EXTRACTION HELPERS
    // ========================================
    
    getFieldValue(fieldName, alternativeFieldName = null) {
      // Try primary field name first
      const primaryElement = this.findCalculatorField(fieldName);
      if (primaryElement && primaryElement.value) {
        return primaryElement.value;
      }
      
      // Try alternative field name (e.g., style_template for shape in styled calculator)
      if (alternativeFieldName) {
        const altElement = this.findCalculatorField(alternativeFieldName);
        if (altElement && altElement.value) {
          return altElement.value;
        }
      }
      
      return null;
    }
    
    getCheckboxValue(fieldName) {
      const element = this.findCalculatorField(fieldName);
      return element ? element.checked : false;
    }
    
    getCurrentQuantity() {
      // Try calculator's method first
      if (this.calculator.getCurrentQuantity) {
        const calculatorQuantity = this.calculator.getCurrentQuantity();
        if (calculatorQuantity > 0) {
          return calculatorQuantity;
        }
      }
      
      // Fallback to direct field access
      const quantityField = this.findCalculatorField('quantity');
      if (quantityField && quantityField.value) {
        return parseInt(quantityField.value, 10) || 0;
      }
      
      // Check wizard quantity input if available
      const wizardQuantity = document.getElementById('ht-wizard-quantity');
      if (wizardQuantity && wizardQuantity.value) {
        return parseInt(wizardQuantity.value, 10) || 0;
      }
      
      return 0;
    }
    
    getAdditionalOptionValue(optionName) {
  // Changed from htCalculatorWizard to htWizard
  if (window.htWizard && window.htWizard.additionalOptions) {
    // SPECIAL HANDLING: multiple_designs needs the count, not boolean
    if (optionName === 'multiple_designs') {
      const isEnabled = window.htWizard.additionalOptions.multiple_designs;
      if (isEnabled) {
        // Get the actual count from the input field
        const countField = document.getElementById('ht-multiple-designs');
        return countField ? (countField.value || '1') : '1';
      } else {
        return '1'; // Default when disabled
      }
    }
    
    // For other options, return the wizard state directly
    return window.htWizard.additionalOptions[optionName] || false;
  }
  
  // Try direct field access as fallback
  const element = this.findCalculatorField(optionName);
  if (element) {
    return element.type === 'checkbox' ? element.checked : element.value;
  }
  
  return false;
}
    
    findCalculatorField(fieldName) {
  if (!this.calculator || !this.calculator.form) return null;
  const normalizedFieldName = fieldName.replace(/_/g, '-');
      
      // Common field ID patterns
      const possibleIds = [
        `ht-${fieldName}-select`,
        `ht-calculator-${fieldName}`,
        `ht-calculator-${fieldName}-select`,
        `ht-${fieldName}`,
        fieldName
      ];
      
      for (const id of possibleIds) {
        const element = document.getElementById(id);
        if (element) {
          return element;
        }
      }
      
      // Try name attribute search
      const byName = this.calculator.form.querySelector(`[name="${fieldName}"]`);
      if (byName) {
        return byName;
      }
      
      return null;
    }
    
    // ========================================
    // CUSTOM SIZE HANDLING
    // ========================================
    
    isCustomSizeSelected() {
  if (this.activeCalculatorType === 'styled') {
    // Styled calculator: check if style_template is "custom"
    const styleTemplate = this.getFieldValue('style_template');
    return styleTemplate && styleTemplate.toLowerCase() === 'custom';
  } else {
    // Regular calculator: check BOTH custom shape AND custom size option
    const shape = this.getFieldValue('shape');
    const size = this.getFieldValue('size');
    
    // Custom shape mode: shape dropdown = "custom"
    const isCustomShape = shape && shape.toLowerCase() === 'custom';
    
    // Custom size mode: size dropdown = "custom_size" (with standard shape)
    const isCustomSizeOption = size && size === 'custom_size';
    
    // Return true if EITHER mode is active
    return isCustomShape || isCustomSizeOption;
  }
}
    
    extractCustomSize() {
      const customSizeData = {};
      
      // Look for width input
      const widthField = document.getElementById('ht-custom-width') || 
                        document.getElementById('ht-calculator-custom-width');
      if (widthField && widthField.value) {
        customSizeData.width = parseFloat(widthField.value);
      }
      
      // Look for height input
      const heightField = document.getElementById('ht-custom-height') || 
                         document.getElementById('ht-calculator-custom-height');
      if (heightField && heightField.value) {
        customSizeData.height = parseFloat(heightField.value);
      }
      
      // Add units if available
      const unitField = document.getElementById('ht-custom-unit') || 
                        document.getElementById('ht-calculator-custom-unit');
      if (unitField && unitField.value) {
        customSizeData.unit = unitField.value;
      } else {
        customSizeData.unit = 'mm'; // Default unit
      }
      
      return customSizeData;
    }
    
    // ========================================
    // PRICING DATA EXTRACTION
    // ========================================
    
    extractPricingData() {
      try {
        // PREFERRED: Read directly from calculator instance (exact values)
        const calc = this.calculator;

        // Styled calculator has lastCalculation with priceExclVAT
        if (calc?.lastCalculation?.priceExclVAT !== undefined) {
          const lc = calc.lastCalculation;
          return {
            totalInclVATPence: Math.round(lc.totalPrice * 100),
            totalExVATPence: Math.round(lc.priceExclVAT * 100),
            vatAmountPence: Math.round(lc.vatAmount * 100),
            pricePerTagPence: Math.round(lc.pricePerTag * 100)
          };
        }

        // Regular calculator has lastPricingData (added for ex-VAT pipeline)
        if (calc?.lastPricingData?.priceExclVAT !== undefined) {
          const lp = calc.lastPricingData;
          const quantity = lp.quantity || 1;
          return {
            totalInclVATPence: Math.round(lp.priceInclVAT * 100),
            totalExVATPence: Math.round(lp.priceExclVAT * 100),
            vatAmountPence: Math.round(lp.vatAmount * 100),
            pricePerTagPence: Math.round((lp.priceInclVAT / quantity) * 100)
          };
        }

        // FALLBACK: Parse from DOM display and derive ex-VAT
        const totalDisplay = document.getElementById('ht-calculator-total-price');
        if (totalDisplay) {
          const priceText = totalDisplay.textContent || totalDisplay.innerText || '';
          const priceMatch = priceText.match(/£([\d,]+\.?\d*)/);
          if (priceMatch) {
            const totalInclVATPounds = parseFloat(priceMatch[1].replace(',', ''));
            const totalInclVATPence = Math.round(totalInclVATPounds * 100);
            const totalExVATPence = Math.round(totalInclVATPence / 1.2);
            const vatAmountPence = totalInclVATPence - totalExVATPence;
            const quantity = this.getCurrentQuantity() || 1;
            return {
              totalInclVATPence,
              totalExVATPence,
              vatAmountPence,
              pricePerTagPence: Math.round(totalInclVATPence / quantity)
            };
          }
        }

        return null;
      } catch (error) {
        console.warn('Error extracting pricing data:', error);
        return null;
      }
    }
    
    // ========================================
    // VALIDATION - CALCULATOR-TYPE-AWARE
    // ========================================
    
    /**
     * Check if element is visible and enabled
     * @param {HTMLElement} element - DOM element to check
     * @returns {boolean} True if element is active
     */
    isFieldActive(element) {
      if (!element) return false;
      
      // Check if disabled
      if (element.disabled) return false;
      
      // Check if hidden (display: none or visibility: hidden)
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      
      // Check parent containers
      let parent = element.parentElement;
      while (parent) {
        const parentStyle = window.getComputedStyle(parent);
        if (parentStyle.display === 'none' || parentStyle.visibility === 'hidden') return false;
        parent = parent.parentElement;
      }
      
      return true;
    }
    
    /**
     * Validate that all required pricing data is present in DOM
     * CALCULATOR-TYPE-AWARE: Different validation for styled vs regular
     * 
     * @returns {Object} Validation result { isValid, errors }
     */
    validatePricingDataInDOM() {
      const errors = [];
      
      // ═══════════════════════════════════════════════════════════════
      // DETECT CALCULATOR TYPE
      // ═══════════════════════════════════════════════════════════════
      const isStyledCalculator = this.activeCalculatorType === 'styled';
      
      
      // ═══════════════════════════════════════════════════════════════
      // VALIDATE MATERIAL DATA (REQUIRED)
      // ═══════════════════════════════════════════════════════════════
      const materialSelect = document.getElementById('ht-material-select');
      const materialOption = materialSelect?.selectedOptions[0];
      
      if (!materialOption || !materialOption.value) {
        errors.push('No material selected');
      } else {
        const requiredMaterialFields = [
          { attr: 'baseCost', displayName: 'Base Cost', allowZero: false },
          { attr: 'setupCost', displayName: 'Setup Cost', allowZero: true },
          { attr: 'gsmOrMic', displayName: 'GSM/Microns', allowZero: false }
        ];
        
        // ⚠️ ONLY validate minSize for REGULAR calculators
        if (!isStyledCalculator) {
          requiredMaterialFields.push({ attr: 'minSize', displayName: 'Min Size', allowZero: false });
        }
        
        requiredMaterialFields.forEach(field => {
          const value = materialOption.dataset[field.attr];
          const isMissing = !value || value === '' || value === 'undefined';
          const isInvalidZero = !field.allowZero && (value === '0' || parseFloat(value) === 0);
          
          if (isMissing || isInvalidZero) {
            errors.push(`Material missing ${field.displayName} (data-${field.attr.replace(/([A-Z])/g, '-$1').toLowerCase()})`);
          }
        });
        
        // Conditional validation: White ink pricing (ONLY if material supports it)
        if (materialOption.dataset.supportsWhiteInk === 'true') {
          const whiteInkPrice = materialOption.dataset.priceWhiteInk;
          if (!whiteInkPrice || whiteInkPrice === '0' || whiteInkPrice === '') {
            errors.push('Material supports white ink but missing priceWhiteInk');
          }
        }
        
        // Conditional validation: Foil pricing (ONLY if material supports it)
        if (materialOption.dataset.supportsFoil === 'true') {
          const foilPrice = materialOption.dataset.priceCmykFoil;
          if (!foilPrice || foilPrice === '0' || foilPrice === '') {
            errors.push('Material supports foil but missing priceCmykFoil');
          }
        }
      }
    
     // VALIDATE SIZE/STYLE TEMPLATE DATA
// ═══════════════════════════════════════════════════════════════
if (isStyledCalculator) {
  // ✅ STYLED CALCULATOR: Validate style template (has dimensions built-in)
  const styleTemplateSelect = document.getElementById('ht-shape-select');
  const styleTemplateOption = styleTemplateSelect?.selectedOptions[0];
  
  if (!styleTemplateOption || !styleTemplateOption.value) {
    errors.push('No style template selected');
  } else {
    // Validate style template has dimension data
    const requiredStyleFields = [
      { attr: 'width', displayName: 'Width' },
      { attr: 'height', displayName: 'Height' }
    ];
    
    requiredStyleFields.forEach(field => {
      const value = styleTemplateOption.dataset[field.attr];
      if (!value || value === '' || value === '0') {
        errors.push(`Style template missing ${field.displayName} (data-${field.attr})`);
      }
    });
    
    // For styled calculators, we also need product type
    if (!styleTemplateOption.dataset.productType) {
      errors.push('Style template missing productType (data-product-type)');
    }
  }
} else {
  // ✅ REGULAR CALCULATOR: Check for custom shape OR custom size
  const shapeSelect = document.getElementById('ht-shape-select');
  const shapeOption = shapeSelect?.selectedOptions[0];
  const shapeValue = shapeOption?.value;
  
  const sizeSelect = document.getElementById('ht-size-select');
  const sizeOption = sizeSelect?.selectedOptions[0];
  const sizeValue = sizeOption?.value;
  
  // 🔥 CRITICAL FIX: Detect BOTH custom shape AND custom size modes
  const isCustomShape = shapeValue && shapeValue.toLowerCase() === 'custom';
  const isCustomSizeOption = sizeValue && sizeValue === 'custom_size';
  
  // ═══════════════════════════════════════════════════════════════
  // CUSTOM SIZE/SHAPE VALIDATION (merged logic)
  // ═══════════════════════════════════════════════════════════════
  if (isCustomShape || isCustomSizeOption) {
    // ✅ CUSTOM MODE: Validate custom dimension inputs
    const customWidthInput = document.getElementById('ht-custom-width');
    const customHeightInput = document.getElementById('ht-custom-height');
    
    if (!customWidthInput || !customHeightInput) {
      errors.push('Custom dimensions selected but input fields not found in DOM');
    } else {
      const width = parseFloat(customWidthInput.value);
      const height = parseFloat(customHeightInput.value);
      
      // Validate positive values exist
      if (!width || width <= 0) {
        errors.push('Custom width is required and must be greater than 0');
      }
      if (!height || height <= 0) {
        errors.push('Custom height is required and must be greater than 0');
      }
      
      // Validate against material constraints
      if (width > 0 && height > 0) {
        const materialSelect = document.getElementById('ht-material-select');
        const materialOption = materialSelect?.selectedOptions[0];
        const minSize = parseFloat(materialOption?.dataset.minSize) || 20;
        const maxWidth = parseFloat(materialOption?.dataset.maxWidth) || 426;
        const maxHeight = parseFloat(materialOption?.dataset.maxHeight) || 296;
        
        if (width < minSize) {
          errors.push(`Custom width must be at least ${minSize}mm`);
        }
        if (height < minSize) {
          errors.push(`Custom height must be at least ${minSize}mm`);
        }
        if (width > maxWidth) {
          errors.push(`Custom width exceeds maximum ${maxWidth}mm`);
        }
        if (height > maxHeight) {
          errors.push(`Custom height exceeds maximum ${maxHeight}mm`);
        }
      }
    }
  } else {
    // ═══════════════════════════════════════════════════════════════
    // STANDARD SIZE MODE: Validate size dropdown selection
    // ═══════════════════════════════════════════════════════════════
    if (!sizeOption || !sizeOption.value || sizeOption.value === '') {
      errors.push('No size selected');
    } else {
      const requiredSizeFields = [
        { attr: 'width', displayName: 'Width' },
        { attr: 'height', displayName: 'Height' },
        { attr: 'tagsPerSheet', displayName: 'Tags Per Sheet' }
      ];
      
      requiredSizeFields.forEach(field => {
        const value = sizeOption.dataset[field.attr];
        if (!value || value === '' || value === '0') {
          errors.push(`Size missing ${field.displayName} (data-${field.attr.replace(/([A-Z])/g, '-$1').toLowerCase()})`);
        }
      });
    }
  }
}
      
      // ═══════════════════════════════════════════════════════════════
      // VALIDATE PRINT SIDES DATA (REQUIRED)
      // ═══════════════════════════════════════════════════════════════
      const printSidesSelect = document.getElementById('ht-print-sides-select');
      const printSidesOption = printSidesSelect?.selectedOptions[0];
      
      if (!printSidesOption || !printSidesOption.value) {
        errors.push('No print sides selected');
      } else {
        if (!printSidesOption.dataset.multiplier) {
          errors.push('Print sides missing multiplier (data-multiplier)');
        }
        if (printSidesOption.dataset.extraCharge === undefined) {
          errors.push('Print sides missing extraCharge (data-extra-charge)');
        }
      }
      
      // ═══════════════════════════════════════════════════════════════
      // VALIDATE PRINT INK DATA (REQUIRED)
      // ═══════════════════════════════════════════════════════════════
      const printInkSelect = document.getElementById('ht-print-ink-select');
      const printInkOption = printInkSelect?.selectedOptions[0];
      
      if (!printInkOption || !printInkOption.value) {
        errors.push('No print ink selected');
      } else {
        if (!printInkOption.dataset.artworkCharge) {
          errors.push('Print ink missing artworkCharge (data-artwork-charge)');
        }
        if (printInkOption.dataset.perSheetCost === undefined) {
          errors.push('Print ink missing perSheetCost (data-per-sheet-cost)');
        }
      }
      
      // ═══════════════════════════════════════════════════════════════
      // VALIDATE OPTIONAL FEATURES (ONLY IF VISIBLE AND SELECTED)
      // ═══════════════════════════════════════════════════════════════
      
      // Corner style (ONLY if field is visible AND has selection)
      const cornerStyleSelect = document.getElementById('ht-corner-style-select');
      if (this.isFieldActive(cornerStyleSelect)) {
        const cornerStyleOption = cornerStyleSelect.selectedOptions[0];
        if (cornerStyleOption && cornerStyleOption.value && cornerStyleOption.value !== '') {
          if (!cornerStyleOption.dataset.pricePerThousand) {
            errors.push('Corner style selected but missing pricePerThousand (data-price-per-thousand)');
          }
          if (cornerStyleOption.dataset.setupCharge === undefined) {
            errors.push('Corner style selected but missing setupCharge (data-setup-charge)');
          }
        }
      }
      
      // Drill diameter (ONLY if field is visible AND has selection)
      const drillDiameterSelect = document.getElementById('ht-drill-diameter-select');
      if (this.isFieldActive(drillDiameterSelect)) {
        const drillDiameterOption = drillDiameterSelect.selectedOptions[0];
        if (drillDiameterOption && drillDiameterOption.value && 
            drillDiameterOption.value !== '' && drillDiameterOption.value !== '0') {
          // Drill diameter selected - now validate position
          const drillPositionSelect = document.getElementById('ht-drill-position-select');
          const drillPositionOption = drillPositionSelect?.selectedOptions[0];
          
          if (!drillPositionOption || !drillPositionOption.value || drillPositionOption.value === '') {
            errors.push('Drill diameter selected but no position selected');
          } else {
            if (drillPositionOption.dataset.setupCharge === undefined) {
              errors.push('Drill position missing setupCharge (data-setup-charge)');
            }
            if (drillPositionOption.dataset.pricePerThousand === undefined) {
              errors.push('Drill position missing pricePerThousand (data-price-per-thousand)');
            }
          }
        }
      }
      
      // Eyelet (ONLY if selected and NOT "none")
      const eyeletSelect = document.getElementById('ht-eyelet-select');
      if (this.isFieldActive(eyeletSelect)) {
        const eyeletOption = eyeletSelect.selectedOptions[0];
        if (eyeletOption && eyeletOption.value && 
            eyeletOption.value !== '' && eyeletOption.value !== 'eyelet_none') {
          if (eyeletOption.dataset.pricePerThousand === undefined) {
            errors.push('Eyelet selected but missing pricePerThousand (data-price-per-thousand)');
          }
          if (eyeletOption.dataset.setupCharge === undefined) {
            errors.push('Eyelet selected but missing setupCharge (data-setup-charge)');
          }
        }
      }
      
      // String attachment (ONLY if selected and NOT "none")
      const stringAttachmentSelect = document.getElementById('ht-string-attachment-select');
      if (this.isFieldActive(stringAttachmentSelect)) {
        const stringAttachmentOption = stringAttachmentSelect.selectedOptions[0];
        if (stringAttachmentOption && stringAttachmentOption.value && 
            stringAttachmentOption.value !== '' && 
            stringAttachmentOption.value !== 'string_attachment_none') {
          
          if (stringAttachmentOption.dataset.attachmentCost === undefined) {
            errors.push('String attachment selected but missing attachmentCost (data-attachment-cost)');
          }
          
          // If string attachment selected, validate string type
          const stringTypeSelect = document.getElementById('ht-string-type-select');
          if (this.isFieldActive(stringTypeSelect)) {
            const stringTypeOption = stringTypeSelect.selectedOptions[0];
            if (stringTypeOption && stringTypeOption.value && stringTypeOption.value !== '') {
              if (stringTypeOption.dataset.pricePerString === undefined) {
                errors.push('String type selected but missing pricePerString (data-price-per-string)');
              }
            }
          }
        }
      }
      
      // ═══════════════════════════════════════════════════════════════
      // RETURN VALIDATION RESULT
      // ═══════════════════════════════════════════════════════════════
      const isValid = errors.length === 0;
      
      if (!isValid) {
        console.error('❌ Pricing data validation failed:', errors);
      } else {
      }
      
      return {
        isValid,
        errors,
        timestamp: Date.now()
      };
    }
    
    /**
     * Export state with validation
     * Only exports if all required pricing data is present
     * 
     * @returns {Object|null} State or null if validation fails
     */
    exportStateWithValidation() {
      const validation = this.validatePricingDataInDOM();
      
      if (!validation.isValid) {
        console.error('❌ Cannot export state - pricing data validation failed');
        console.error('Missing/invalid fields:', validation.errors);
        
        // Show user-friendly error
        this.showValidationError(validation.errors);
        
        return null;
      }
      
      return this.extractState();
    }
    
    /**
     * Show validation error to user
     * @param {Array} errors - Array of error messages
     */
    showValidationError(errors) {
      const errorContainer = document.querySelector('.product-form__error-message-wrapper');
      if (errorContainer) {
        const errorElement = errorContainer.querySelector('.product-form__error-message');
        if (errorElement) {
          errorElement.innerHTML = `
            <strong>Configuration Error:</strong><br>
            Missing required pricing data from metaobjects:<br>
            <ul style="margin: 8px 0; padding-left: 20px;">
              ${errors.map(err => `<li>${err}</li>`).join('')}
            </ul>
            Please check your metaobject configuration in Shopify admin.
          `;
          errorContainer.removeAttribute('hidden');
          
          // Auto-hide after 10 seconds
          setTimeout(() => {
            errorContainer.setAttribute('hidden', '');
          }, 10000);
        }
      }
    }
    
    // ========================================
    // SESSIONSTORE PERSISTENCE
    // ========================================
    
    saveState() {
      try {
        const state = this.extractState();
        if (state) {
          sessionStorage.setItem(this.storageKey, JSON.stringify(state));
          
          // Dispatch event for other modules
          document.dispatchEvent(new CustomEvent('calculator:state-saved', {
            detail: { state }
          }));
        }
      } catch (error) {
        console.error('❌ Error saving state:', error);
      }
    }
    
    /**
     * Sanitize recovered state to fix invalid values
     * @param {Object} state - The recovered state object
     * @returns {Object} Sanitized state
     */
    sanitizeRecoveredState(state) {
      // Fix "unknown" material values
      if (state.material === 'unknown') {
        console.warn('🧹 Sanitizing material: "unknown" → null');
        state.material = null;
      }
      
      // Fix empty strings to null for consistency
      const fieldsToSanitize = ['shape', 'size', 'print_sides', 'print_ink'];
      fieldsToSanitize.forEach(field => {
        if (state[field] === '' || state[field] === 'unknown') {
          console.warn(`🧹 Sanitizing ${field}: "${state[field]}" → null`);
          state[field] = null;
        }
      });
      
      return state;
    }
    
    recoverState() {
      try {
        const savedStateJson = sessionStorage.getItem(this.storageKey);
        if (!savedStateJson) {
          return null;
        }
        
        let savedState = JSON.parse(savedStateJson);
        
        // Validate state age (max 30 minutes)
        const stateAge = Date.now() - savedState.timestamp;
        const maxAge = 30 * 60 * 1000; // 30 minutes
        
        if (stateAge > maxAge) {
          this.clearState();
          return null;
        }
        
        // Sanitize recovered state to fix invalid values
        savedState = this.sanitizeRecoveredState(savedState);
        
        return savedState;
      } catch (error) {
        console.error('❌ Error recovering state:', error);
        return null;
      }
    }
    
    clearState() {
      sessionStorage.removeItem(this.storageKey);
      
      // Dispatch event for other modules
      document.dispatchEvent(new CustomEvent('calculator:state-cleared'));
    }
    
    // ========================================
    // SILENT RECOVERY WITH NOTIFICATION
    // ========================================
    
    attemptStateRecovery() {
      const recoveredState = this.recoverState();
      
      if (recoveredState && this.calculator) {
        // Show subtle notification on the screen (commented out for production)
        // this.showRecoveryNotification();
        
        // Dispatch recovery event for other modules to handle restoration
        document.dispatchEvent(new CustomEvent('calculator:state-recovered', {
          detail: { state: recoveredState }
        }));
        
      }
    }
    
    showRecoveryNotification() {
      // Remove any existing notification
      this.removeRecoveryNotification();
      
      // Create subtle notification
      const notification = document.createElement('div');
      notification.id = this.recoveryNotificationId;
      notification.className = 'ht-recovery-notification';
      notification.innerHTML = `
        <div class="ht-recovery-notification__content">
          <span class="ht-recovery-notification__icon">🔄</span>
          <span class="ht-recovery-notification__text">Calculator restored from previous session</span>
        </div>
      `;
      
      // Add CSS for notification
      this.injectRecoveryNotificationStyles();
      
      // Insert notification
      const calculatorWrapper = document.querySelector('.ht-calculator-wrapper') || document.body;
      calculatorWrapper.insertBefore(notification, calculatorWrapper.firstChild);
      
      // Auto-hide after 3 seconds
      setTimeout(() => {
        this.removeRecoveryNotification();
      }, 3000);
    }
    
    removeRecoveryNotification() {
      const existing = document.getElementById(this.recoveryNotificationId);
      if (existing) {
        existing.remove();
      }
    }
    
    injectRecoveryNotificationStyles() {
      const styleId = 'ht-recovery-notification-styles';
      
      // Don't inject if already exists
      if (document.getElementById(styleId)) {
        return;
      }
      
      const styles = document.createElement('style');
      styles.id = styleId;
      styles.textContent = `
        .ht-recovery-notification {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          background: #4CAF50;
          color: white;
          padding: 12px 16px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 14px;
          font-weight: 500;
          animation: ht-recovery-slide-in 0.3s ease-out;
          max-width: 300px;
        }
        
        .ht-recovery-notification__content {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .ht-recovery-notification__icon {
          font-size: 16px;
        }
        
        @keyframes ht-recovery-slide-in {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @media (max-width: 768px) {
          .ht-recovery-notification {
            top: 10px;
            right: 10px;
            left: 10px;
            max-width: none;
          }
        }
      `;
      
      document.head.appendChild(styles);
    }
    
    // ========================================
    // PUBLIC API METHODS
    // ========================================
    
    /**
     * Extract current calculator state
     * @returns {Object|null} Current state or null if no calculator
     */
    exportState() {
      return this.extractState();
    }
    
    /**
     * Get cached state without validation
     * @returns {Object|null} Cached state from last extraction
     */
    getCachedState() {
      // This method is called by wizard during sync operations
      // Return the last extracted state if available
      return this.extractState();
    }
    
    /**
     * Manually save state to sessionStorage
     * @returns {boolean} Success status
     */
    saveStateManually() {
      try {
        this.saveState();
        return true;
      } catch (error) {
        console.error('❌ Manual save failed:', error);
        return false;
      }
    }
    
    /**
     * Get recovered state without triggering recovery flow
     * @returns {Object|null} Recovered state or null
     */
    getRecoveredState() {
      return this.recoverState();
    }
    
    /**
     * Clear all saved state
     * @returns {boolean} Success status
     */
    clearSavedState() {
      try {
        this.clearState();
        return true;
      } catch (error) {
        console.error('❌ Clear failed:', error);
        return false;
      }
    }
    
    /**
     * Get current calculator type and status
     * @returns {Object} Calculator info
     */
    getCalculatorInfo() {
      return {
        type: this.activeCalculatorType,
        hasCalculator: !!this.calculator,
        calculatorReady: !!(this.calculator && this.calculator.form)
      };
    }
    
    // ========================================
    // UTILITY METHODS
    // ========================================
    
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }
  }

  // ========================================
  // MODULE INITIALIZATION & EXPORT
  // ========================================

  // Initialize state manager when DOM is ready
  let htStateManager = null;

  document.addEventListener('DOMContentLoaded', () => {
    // Wait a bit for calculators to initialize
    setTimeout(() => {
      htStateManager = new HTCalculatorStateManager();
      
      // Make available globally for debugging and external access
      window.htStateManager = htStateManager;
      
      // Dispatch ready event
      document.dispatchEvent(new CustomEvent('ht-state-manager:ready'));
      
    }, 500);
  });

  // Export the class to prevent future double loading
  window.HTCalculatorStateManager = HTCalculatorStateManager;

})();